import React, { useState } from "react";
import { Card, Steps, Button, Row, Col, Space, Typography, notification, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import SelectDates from "./Steps/SelectDates";
import SelectApplication from "./Steps/SelectApplication";
import SelectIntent from "./Steps/SelectIntent";
import SelectSentinels from "./Steps/SelectSentinels";
import Review from "./Steps/Review";
import { EvaluationData } from "./EvaluationData";
import { ApplicationAction } from "../../model_gen/application";
import { Application } from "../../model_gen/application";
import { EvaluationService } from "../../services/EvaluationService";
import { usePermissions } from "../../PermissionsProvider";

const { Step } = Steps;
const { Text } = Typography;

const EvaluationNew: React.FC = () => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [evaluationData, setEvaluationData] = useState<EvaluationData>({
    sentinels: [],
  });

  const steps = [
    {
      title: "Application",
      description: "Choose app",
      component: (
        <SelectApplication
          value={evaluationData.application}
          onChange={(application: Application) => 
            setEvaluationData(prev => ({ ...prev, application }))
          }
        />
      ),
    },
    {
      title: "Intent",
      description: "Select intent",
      component: (
        <SelectIntent
          application={evaluationData.application}
          value={evaluationData.intent}
          onChange={(intent: ApplicationAction) => 
            setEvaluationData(prev => ({ ...prev, intent }))
          }
        />
      ),
    },
    {
      title: "Dates",
      description: "Select time range",
      component: (
        <SelectDates
          startDate={evaluationData.dateStart}
          endDate={evaluationData.dateEnd}
          onChange={(dateStart, dateEnd) => 
            setEvaluationData(prev => ({ ...prev, dateStart, dateEnd }))
          }
          applicationId={evaluationData?.application?.id}
          intentId={evaluationData?.intent?.id}
        />
      ),
    },
    {
      title: "Sentinels",
      description: "Configure sentinels",
      component: (
        <SelectSentinels
          application={evaluationData.application}
          intent={evaluationData.intent}
          onChange={(sentinels) => 
            setEvaluationData(prev => ({ ...prev, sentinels }))
          }
        />
      ),
    },
    {
      title: "Review",
      description: "Confirm settings",
      component: (
        <Review
          evaluationData={evaluationData}
        />
      ),
    },
  ];

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return evaluationData.application;
      case 1:
        return evaluationData.intent;
      case 2:
        return evaluationData.dateStart && evaluationData.dateEnd;
      case 3:
        return evaluationData.sentinels.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const runEvaluation = async () => {
    setLoading(true);
    const payload = {
      date_start: evaluationData.dateStart?.getTime(),
      date_end: evaluationData.dateEnd?.getTime(),
      application_id: evaluationData.application.id,
      intent_id: evaluationData.intent.id,
      sentinels: evaluationData.sentinels
    };
    EvaluationService.runBatchEvaluation(payload, null, apiKey).subscribe((response: any) => {
      // Return to /evaluation
      navigate("/evaluation");
      setLoading(false);
    }, (error: any) => {
      notification.error({
        message: "Error",
        description: `Failed to run evaluation: ${error}`,
      });
      setLoading(false);
    });
  };

  return (
    <Card className="applications-wrapper">
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1 style={{ margin: 0 }}>
              <Space>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>Evaluations</span>
                <span style={{ color: "rgba(0, 0, 0, 0.45)" }}>/</span>
                <span>New Evaluation</span>
              </Space>
            </h1>
            <Text type="secondary" style={{ display: "block", marginBottom: "24px" }}>
              Step {currentStep + 1} of {steps.length}
            </Text>
          </Col>
        </Row>

        <Steps 
          current={currentStep} 
          progressDot 
          style={{ margin: "12px 0" }}
        >
          {steps.map(step => (
            <Step 
              key={step.title} 
              title={step.title} 
              description={step.description}
            />
          ))}
        </Steps>

        <Card 
          bordered={false} 
          className="step-content"
          style={{ 
            padding: "24px",
            borderRadius: "8px",
            margin: "12px 0"
          }}
        >
          {steps[currentStep].component}
        </Card>

        <Row justify="space-between">
          <Col>
            {currentStep > 0 && (
              <Button 
                size="large"
                onClick={handlePrev}
                disabled={loading}
              >
                Previous Step
              </Button>
            )}
          </Col>
          <Col>
            {currentStep < steps.length - 1 ? (
              <Button 
                type="primary"
                size="large"
                onClick={handleNext}
                disabled={!canProceed() || loading}
              >
                Continue
              </Button>
            ) : (
              <Button 
                type="primary"
                size="large"
                disabled={!canProceed() || loading}
                onClick={runEvaluation}
                icon={loading && <Spin size="small" />}
              >
                {loading ? "Running evaluation..." : "Run Evaluation"}
              </Button>
            )}
          </Col>
        </Row>
      </Space>
    </Card>
  );
};

export default EvaluationNew;
